import { supabase } from '../supabaseClient';
import {
  DAILY_NOTE_VIEW_LOCK_USER_META_KEY,
  dailyNoteViewLockFromDbRow,
  dailyNoteViewLockNeedsRemoteSync,
  dailyNoteViewLockToDbPayload,
  dailyNoteViewLockToUserMetaPayload,
  hydrateDailyNoteViewLock,
  parseDailyNoteViewLockMeta,
  type DailyNoteViewLockState,
} from '../utils/dailyNoteViewLockStorage';

function isMissingRelationError(message: string): boolean {
  return /relation .* does not exist|Could not find the table|schema cache/i.test(message);
}

async function persistDailyNoteViewLockUserMeta(state: DailyNoteViewLockState): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    data: {
      [DAILY_NOTE_VIEW_LOCK_USER_META_KEY]: dailyNoteViewLockToUserMetaPayload(state),
    },
  });
  if (error) throw new Error(error.message);
}

async function fetchDailyNoteViewLockUserMeta(): Promise<DailyNoteViewLockState | null> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) return null;
  return parseDailyNoteViewLockMeta(data.user.user_metadata as Record<string, unknown> | undefined);
}

/** DB 행을 읽는다. 테이블이 아직 없으면 이전 계정 메타데이터로 폴백한다. */
export async function fetchDailyNoteViewLockRemote(
  userId: string
): Promise<DailyNoteViewLockState | null> {
  const uid = userId.trim();
  if (!uid) return null;
  try {
    const { data, error } = await supabase
      .from('daily_note_view_lock')
      .select('enabled, password_hash, updated_at')
      .eq('user_id', uid)
      .maybeSingle();
    if (error) {
      if (isMissingRelationError(error.message)) {
        return fetchDailyNoteViewLockUserMeta();
      }
      return null;
    }
    const fromTable = dailyNoteViewLockFromDbRow(data);
    if (fromTable) return fromTable;
    return fetchDailyNoteViewLockUserMeta();
  } catch {
    return fetchDailyNoteViewLockUserMeta();
  }
}

/** 보기 잠금을 DB에 저장한다. 테이블이 없으면 계정 메타데이터로 폴백한다. */
export async function persistDailyNoteViewLockRemote(
  userId: string,
  state: DailyNoteViewLockState
): Promise<void> {
  const uid = userId.trim();
  if (!uid) return;
  const payload = dailyNoteViewLockToDbPayload(uid, state);
  try {
    const { error } = await supabase.from('daily_note_view_lock').upsert(payload, {
      onConflict: 'user_id',
    });
    if (error) {
      if (isMissingRelationError(error.message)) {
        await persistDailyNoteViewLockUserMeta(state);
        return;
      }
      throw new Error(error.message);
    }
  } catch (e) {
    if (e instanceof Error && isMissingRelationError(e.message)) {
      await persistDailyNoteViewLockUserMeta(state);
      return;
    }
    throw e;
  }
}

/** LS를 즉시 쓰고, DB에서 최신 잠금을 가져와 맞춘다. */
export async function syncDailyNoteViewLockFromRemote(
  userId: string
): Promise<DailyNoteViewLockState> {
  const uid = userId.trim();
  const remote = await fetchDailyNoteViewLockRemote(uid);
  const merged = hydrateDailyNoteViewLock(uid, remote);
  if (dailyNoteViewLockNeedsRemoteSync(merged, remote)) {
    try {
      await persistDailyNoteViewLockRemote(uid, merged);
    } catch {
      /* 이 기기 LS는 유지 */
    }
  }
  return merged;
}
